// =============================================================================
// ApplyToScopeDialog — Phase 4.0 bulk-apply scope picker
// -----------------------------------------------------------------------------
// Pops up after a per-variant override change (background image uploads and
// print-area saves) so the merchant can decide whether to apply the new value
// to:
//
//   • This variant only            (single-row upsert; already saved by caller)
//   • All "<Value>" variants       (per option axis — e.g. all variants where
//                                   Color = Black, or where Size = M)
//   • All variants                 (every row in the active print area)
//
// One scope button is rendered for EVERY option axis on the source row
// (Color, Size, …) so merchants can broadcast by whichever axis the change
// actually depends on — print-area overrides typically follow Size, mockup
// images typically follow Color. An axis scope is hidden when:
//   • Only one variant matches the value (option would equal "current"), or
//   • Every variant matches the value (option would duplicate "All variants").
//
// This component is presentational; it never talks to the API. The parent
// receives a `Scope` value via `onApply` and is responsible for issuing the
// atomic `bulkUpsertVariantConfigs` request.
// =============================================================================

import { useMemo } from 'react';
import { Modal } from '@tkpod/ui';
import type {
  VariantConfigRow,
  VariantOptionValueLabel,
} from '@/lib/api/apparel-sides';

export type ApplyScope =
  | { kind: 'current' }
  | {
      kind: 'option';
      /** Stable identity of the option axis — see {@link getOptionAxisKey}. */
      axisKey: string;
      /** Human label of the axis ("Color", "Size", …) for toasts/subtitles. */
      optionTypeName: string;
      /** The option value shared by all target variants (e.g. "Black"). */
      optionValue: string;
    }
  | { kind: 'all' };

export type ApplyChangeType =
  | 'backgroundImage'
  | 'backgroundColor'
  | 'customizationMandatory'
  | 'printAreaOverride';

const CHANGE_LABELS: Record<ApplyChangeType, string> = {
  backgroundImage: 'mockup override',
  backgroundColor: 'background color',
  customizationMandatory: 'customization requirement',
  printAreaOverride: 'print area override',
};

/**
 * Stable identity for an option axis. `optionTypeId` is the canonical key;
 * slug and name are progressively weaker fallbacks for legacy rows whose
 * OptionType linkage is incomplete. Never returns an empty string for a
 * labelled option value (the `value` itself is non-empty by contract).
 */
export function getOptionAxisKey(ov: VariantOptionValueLabel): string {
  return ov.optionTypeId ?? ov.optionTypeSlug ?? ov.optionTypeName ?? '';
}

/**
 * Whether a matrix row carries the given option value on the given axis.
 * Shared by the dialog (match counts) and the parent's bulk-apply target
 * filter so both always agree on the target set.
 */
export function rowMatchesOptionScope(
  row: VariantConfigRow,
  axisKey: string,
  optionValue: string,
): boolean {
  return row.optionValues.some(
    (ov) => getOptionAxisKey(ov) === axisKey && ov.value === optionValue,
  );
}

interface AxisScopeOption {
  axisKey: string;
  optionTypeName: string;
  optionValue: string;
  matchCount: number;
}

export interface ApplyToScopeDialogProps {
  open: boolean;
  changeType: ApplyChangeType;
  /** The row whose value is being propagated. */
  sourceRow: VariantConfigRow;
  /** Every row currently rendered in the active print area's matrix. */
  allRows: VariantConfigRow[];
  /** Localized label of the active print area, used for context only. */
  printAreaLabel: string;
  /** Whether the bulk-apply request is currently in-flight. */
  isApplying: boolean;
  onApply: (scope: ApplyScope) => void;
  onCancel: () => void;
}

export function ApplyToScopeDialog({
  open,
  changeType,
  sourceRow,
  allRows,
  printAreaLabel,
  isApplying,
  onApply,
  onCancel,
}: ApplyToScopeDialogProps) {
  const total = allRows.length;

  // One candidate scope per option axis on the source row, in the row's
  // canonical option order (server sorts by OptionType.sortOrder, so
  // Color/Size order matches the matrix labels). Axes whose match set is
  // degenerate (only the source row, or every row) are filtered out.
  const axisScopes = useMemo<AxisScopeOption[]>(() => {
    const seen = new Set<string>();
    const scopes: AxisScopeOption[] = [];
    for (const ov of sourceRow.optionValues) {
      const axisKey = getOptionAxisKey(ov);
      if (!axisKey || seen.has(axisKey)) continue;
      seen.add(axisKey);

      const matchCount = allRows.filter((r) =>
        rowMatchesOptionScope(r, axisKey, ov.value),
      ).length;
      if (matchCount <= 1 || matchCount >= total) continue;

      scopes.push({
        axisKey,
        optionTypeName: ov.optionTypeName ?? 'Option',
        optionValue: ov.value,
        matchCount,
      });
    }
    return scopes;
  }, [sourceRow.optionValues, allRows, total]);

  const showAllScope = total > 1;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="md"
      title={`Apply ${CHANGE_LABELS[changeType]}`}
      description={`Where should this ${CHANGE_LABELS[changeType]} be applied on ${printAreaLabel}?`}
    >
      <div className="space-y-2">
        <ScopeButton
          title="This variant only"
          subtitle={`Apply to ${sourceRow.optionsLabel}`}
          variant="primary"
          disabled={isApplying}
          onClick={() => onApply({ kind: 'current' })}
        />

        {axisScopes.map((axis) => (
          <ScopeButton
            key={axis.axisKey}
            title={`All "${axis.optionValue}" variants`}
            subtitle={`Apply to ${axis.matchCount} variants where ${axis.optionTypeName} = ${axis.optionValue}`}
            disabled={isApplying}
            onClick={() =>
              onApply({
                kind: 'option',
                axisKey: axis.axisKey,
                optionTypeName: axis.optionTypeName,
                optionValue: axis.optionValue,
              })
            }
          />
        ))}

        {showAllScope && (
          <ScopeButton
            title="All variants"
            subtitle={`Apply to all ${total} variants on ${printAreaLabel}`}
            disabled={isApplying}
            onClick={() => onApply({ kind: 'all' })}
          />
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isApplying}
          className="text-sm text-text-secondary transition-colors hover:text-text-primary disabled:opacity-50"
        >
          {isApplying ? 'Applying…' : 'Cancel'}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Internal — single scope option button
// ---------------------------------------------------------------------------

function ScopeButton({
  title,
  subtitle,
  variant = 'default',
  disabled,
  onClick,
}: {
  title: string;
  subtitle: string;
  variant?: 'default' | 'primary';
  disabled: boolean;
  onClick: () => void;
}) {
  const isPrimary = variant === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'w-full rounded-lg border px-4 py-3 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        isPrimary
          ? 'border-accent bg-accent/5 hover:bg-accent/10'
          : 'border-border hover:border-accent hover:bg-accent/5',
      ].join(' ')}
    >
      <p className="text-sm font-semibold text-text-primary">{title}</p>
      <p className="mt-0.5 text-xs text-text-secondary">{subtitle}</p>
    </button>
  );
}
